import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'

import { useAuthContext } from 'features/auth/auth-context'
import { useSignoutConfirmation } from 'features/auth/use-signout-confirmation'
import { useBugReportUIStore } from 'features/bug-report/store'
import {
  buildPaletteCommands,
  collectHubSummaries,
} from 'features/project/features/command-palette/build-commands'
import type { PaletteCommand } from 'features/project/features/command-palette/types'
import {
  getNavigationItems,
  type PageSettings,
} from 'features/project/partials/project-layout/components/navigation/utils'
import { useProjectLayoutState } from 'features/project/partials/project-layout/hooks/use-project-layout-state'
import {
  useAskYogiAnywhere,
  useNav,
} from 'features/project/partials/project-layout/use-layout'
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import { SETTINGS_NAV_ITEMS } from 'features/settings/nav-config'
import {
  ClientAuthPolicy,
  getEffectiveRole,
  isRoleAllowed,
} from 'src/types/auth-role'
import mixpanel from 'trackers/mixpanel'
import type { Project } from 'types/project'

type StonlyWindow = Window & {
  StonlyWidget?: (...args: unknown[]) => unknown
}

export const useCommandPaletteCommands = ({
  isProjectLocked,
}: {
  isProjectLocked: boolean
}): PaletteCommand[] => {
  const { projectId, details, feature_flags, projectsData } =
    useProjectLayoutState()
  const { userDetails } = useAuthContext()
  const { toggleMainNav } = useNav()
  const { toggleAskYogiAnywhere } = useAskYogiAnywhere()
  const navigate = useNavigate()
  const signOut = useSignoutConfirmation()

  const featureFlags = feature_flags as FeatureFlagsResponse | undefined
  const flags = feature_flags as Record<string, boolean> | undefined
  const pageSettings = (details?.clientSettings?.project?.pages ??
    {}) as PageSettings
  const effectiveRole = getEffectiveRole(
    details?.role,
    !!userDetails?.yogi_user,
    userDetails?.role,
  )

  const navigationItems = getNavigationItems({
    feature_flags: featureFlags,
    pageSettings,
    isYogiUser: !!userDetails?.yogi_user,
    projectId,
  }).filter((item) => item.enabled)

  const settingsItems = SETTINGS_NAV_ITEMS.filter(
    (item) =>
      isRoleAllowed(effectiveRole, item.roleAllowlist) &&
      !pageSettings?.[item.pageKey]?.hidden,
  )

  const hubs = collectHubSummaries(
    (projectsData as { data?: Project[] } | undefined)?.data ?? [],
  )

  return buildPaletteCommands({
    projectId,
    navigationItems,
    // hide_ask_yogi: This is to hide Ask Yogi for Disney. Delete these checks once they approve Ask Yogi usage.
    showHome: !flags?.['hide_ask_yogi'],
    settingsItems,
    hubs,
    // The create flow lives on the v2 dashboards list; v1-only hubs don't
    // get the command.
    canCreateDashboard: !!flags?.['enable_charts_2'],
    canCreateAlert: !pageSettings.alerts?.hidden,
    canOpenAskYogiAnywhere: !isProjectLocked && !flags?.['hide_ask_yogi'],
    canNewAskYogiChat:
      !pageSettings.chadgent?.hidden && !flags?.['hide_ask_yogi'],
    canSignOut: isRoleAllowed(
      effectiveRole,
      ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
    ),
    navigate,
    // Hard reload, same as HubSelector — resets all per-hub store state.
    switchHub: (uuid) => {
      window.location.href = '/project/' + uuid
    },
    toggleAskYogiAnywhere,
    toggleMainNav,
    signOut,
    reportBug: () => useBugReportUIStore.getState().requestOpen(),
    copyProjectUuid: () => {
      navigator.clipboard
        .writeText(projectId)
        .then(() => toast.info('Project UUID copied to clipboard'))
    },
    openHelp: () => {
      mixpanel.track('help', { proj_uuid: projectId })
      ;(window as StonlyWindow).StonlyWidget?.('setYOffset', 0)
    },
  })
}
